import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'rexue');
  const [comics, setComics] = useState<Comic[]>([]);

  useEffect(() => {
    setActiveCategory(slug || 'rexue');
  }, [slug]);

  useEffect(() => {
    setComics(getComicsByCategory(activeCategory));
  }, [activeCategory]);

  const currentCategory = getCategoryBySlug(activeCategory);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <title>{currentCategory.name}漫画 - 非常爱漫画免费在线看 | 爱漫画</title>
      <meta name="description" content={`非常爱漫画提供海量${currentCategory.name}漫画免费在线看，${currentCategory.description}。爱漫画平台免费漫画大全随时畅读。`} />
      <meta name="keywords" content={`${currentCategory.name}漫画, 免费${currentCategory.name}漫画, ${currentCategory.name}漫画在线看, 爱漫画, 非常爱漫画`} />
      <link rel="canonical" href={`https://www.aimanhua.com/category/${activeCategory}`} />
      <meta property="og:title" content={`${currentCategory.name}漫画 - 非常爱漫画免费在线看 | 爱漫画`} />
      <meta property="og:description" content={`非常爱漫画提供海量${currentCategory.name}漫画免费在线看，${currentCategory.description}。`} />
      <meta property="og:url" content={`https://www.aimanhua.com/category/${activeCategory}`} />
      <meta property="og:type" content="website" />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span>/</span>
                <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
              </nav>
            </div>
          </div>

          <div className="bg-background-50 border-b border-background-200/70">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8 md:py-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-2xl md:text-3xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {currentCategory.name}漫画
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">{currentCategory.description}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-600">
                共找到 <strong className="text-primary-600">{comics.length}</strong> 部{currentCategory.name}漫画作品
              </p>
            </div>
          </div>

          <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-3 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-primary-600'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {comics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - ${currentCategory.name}漫画免费在线看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-primary-500 text-white text-xs font-medium">
                      {comic.rating}
                    </div>
                    {comic.status === '连载中' && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-accent-500 text-white text-xs font-medium">
                        连载
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1">{comic.author} · {comic.chapters.length}话</p>
                </Link>
              ))}
            </div>

            {comics.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                  <i className="ri-book-2-line text-3xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无漫画作品</p>
                <Link to="/" className="text-primary-600 hover:text-primary-700 mt-3 inline-block text-sm cursor-pointer">
                  返回首页查看更多
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}